import { isPieChartEnhancementsEnabled } from '@thoughtspot/session-service';
import _ from 'lodash';
import { getEnvFlags } from '../../env';
export var SerieType;
(function (SerieType) {
    SerieType["Column"] = "column";
    SerieType["Bar"] = "bar";
    SerieType["Area"] = "area";
    SerieType["Line"] = "line";
    SerieType["Pie"] = "pie";
    SerieType["Funnel"] = "funnel";
    SerieType["Heatmap"] = "heatmap";
    SerieType["Treemap"] = "treemap";
    SerieType["Scatter"] = "scatter";
    SerieType["CandleStick"] = "candlestick";
    SerieType["Waterfall"] = "waterfall";
})(SerieType || (SerieType = {}));
const borderColor = '#2770EF';
const borderWidth = 2;
const fullOpacity = 1;
const fadedOpacity = 0.2;
const noOpacity = 0;
const inactiveStateProps = {
    enabled: true,
    opacity: 1,
    animation: {
        duration: 250,
    },
};
const clipProps = {
    clip: false,
};
const markerRadiusProps = {
    radius: 7,
};
const getBorderStyles = (color) => ({
    border: `1px solid ${color}`,
    borderLeft: `7px solid ${color}`,
});
const hoverLineMarkerStateProps = {
    enabled: true,
    lineColor: borderColor,
    lineWidth: borderWidth,
};
const selectLineMarkerStateProps = {
    enabled: true,
    fillColor: undefined,
    lineColor: borderColor,
    lineWidth: borderWidth,
};
const hoverColumnStateProps = {
    enabled: true,
    brightness: 0,
    borderColor,
    borderWidth,
};
const hoverColumnLineStateProps = {
    enabled: true,
    brightness: 0,
    lineColor: borderColor,
    lineWidth: borderWidth,
};
const selectColumnStateProps = {
    enabled: true,
    color: null,
    borderColor,
    borderWidth,
};
const selectColumnLineStateProps = {
    enabled: true,
    color: null,
    lineColor: borderColor,
    lineWidth: borderWidth,
};
const isAccumulateClick = event => {
    return event.ctrlKey || event.metaKey || event.shiftKey;
};
const addStroke = point => {
    point.graphic.attr({
        fill: point.color,
        'stroke-width': borderWidth,
        stroke: borderColor,
        opacity: fullOpacity,
    });
};
const updateClipProperty = (chart, enabled) => {
    if (chart.types.includes(SerieType.Heatmap) ||
        chart.types.includes(SerieType.Treemap)) {
        return;
    }
    const serie = chart.series.length > 0 ? chart.series[0] : null;
    if (!serie || serie.options.clip === enabled) {
        return;
    }
    if (chart.types.length === 2) {
        if (chart.types.includes(SerieType.Column) &&
            chart.types.includes(SerieType.Line)) {
            chart.update({
                plotOptions: {
                    column: {
                        clip: enabled,
                    },
                },
            }, true);
        }
        return;
    }
    if (chart.types.includes(SerieType.Column)) {
        chart.update({
            plotOptions: {
                column: {
                    clip: enabled,
                },
            },
        }, true);
        return;
    }
    if (chart.types.includes(SerieType.Bar)) {
        chart.update({
            plotOptions: {
                bar: {
                    clip: enabled,
                },
            },
        }, true);
        return;
    }
    if (chart.types.includes(SerieType.CandleStick)) {
        chart.update({
            plotOptions: {
                series: {
                    clip: enabled,
                },
            },
        }, true);
    }
};
const setPointHoverState = p => {
    if (isPieChartEnhancementsEnabled()) {
        p.series.chart.series.forEach(s => {
            const point = s.points[p.index];
            if (point) {
                point.setState('hover');
            }
        });
    }
};
const isPointNoOpacityByDefault = serie => {
    return serie.type === SerieType.Line || serie.type === SerieType.Area;
};
const getDefaultPointOpacity = (serie) => {
    if (isPointNoOpacityByDefault(serie)) {
        return noOpacity;
    }
    return fullOpacity;
};
const getUnselectedPointOpacity = (serie) => {
    if (isPointNoOpacityByDefault(serie)) {
        return noOpacity;
    }
    return fadedOpacity;
};
const applyPointOpacity = (p, opacity) => {
    if (p.graphic.opacity === opacity) {
        return;
    }
    p.graphic.attr({
        opacity,
    });
    if (p.series.options.dataLabels.enabled) {
        p.dataLabel.css({
            opacity,
        });
    }
};
const updatePointOpacity = (p, opacity) => {
    if (p.opacity === opacity) {
        return;
    }
    let dataLabels = {};
    if (p.series.options.dataLabels.enabled) {
        dataLabels = {
            dataLabels: {
                style: {
                    opacity,
                },
            },
        };
    }
    p.update({
        opacity,
        ...dataLabels,
    }, false);
};
const updatePointFullOpacity = p => {
    if (p.opacity === fullOpacity) {
        return;
    }
    let dataLabels = {};
    if (p.series.options.dataLabels.enabled) {
        dataLabels = {
            dataLabels: {
                style: {
                    opacity: fullOpacity,
                },
            },
        };
    }
    p.update({
        opacity: fullOpacity,
        ...dataLabels,
    }, false);
};
const updatePointFadedOpacity = p => {
    if (p.opacity === fadedOpacity) {
        return;
    }
    let dataLabels = {};
    if (p.series.options.dataLabels.enabled) {
        dataLabels = {
            dataLabels: {
                style: {
                    opacity: fadedOpacity,
                },
            },
        };
    }
    p.update({
        opacity: fadedOpacity,
        ...dataLabels,
    }, false);
};
const updateSerieFullOpacity = (s, reflow = false) => {
    if (s.opacity === fullOpacity) {
        return;
    }
    s.update({
        opacity: fullOpacity,
    }, false);
    if (reflow) {
        s.chart.ultraLightRedraw(false, true);
    }
    else if (s.options.dataLabels.enabled) {
        s.dataLabelsGroup.css({
            opacity: fullOpacity,
        });
    }
};
const updateSerieFadedOpacity = (s, reflow = false) => {
    if (s.opacity === fadedOpacity) {
        return;
    }
    s.update({
        opacity: fadedOpacity,
    }, false);
    if (reflow) {
        s.chart.ultraLightRedraw(false, true);
    }
    else if (s.options.dataLabels.enabled) {
        s.dataLabelsGroup.css({
            opacity: fadedOpacity,
        });
    }
};
const getKpiHighchartObj = (chartTmp) => {
    let chart = chartTmp;
    if (chartTmp.getHighchartObj) {
        chart = chartTmp.getHighchartObj();
    }
    return chart;
};
const chartTypeRequiredToApplyOpacity = (chart) => {
    if (chart.types.length === 1) {
        const type = chart.types[0];
        if (type === SerieType.Line ||
            type === SerieType.Scatter ||
            type === SerieType.Heatmap ||
            type === SerieType.Treemap) {
            return true;
        }
        return false;
    }
    if (chart.types.length === 2) {
        if (chart.types.includes(SerieType.Line)) {
            return true;
        }
    }
    return false;
};
const isHeatMapChart = chart => {
    if (chart.types.length === 1) {
        if (chart.types[0] === SerieType.Heatmap) {
            return true;
        }
    }
    return false;
};
const enableTooltip = (chart) => {
    if (!chart.options.tooltip.enable || chart.options.tooltip.enabled) {
        return;
    }
    chart.tooltip.update({
        enabled: true,
    });
};
const disableTooltip = (chart) => {
    if (!chart.options.tooltip.enabled) {
        return;
    }
    chart.tooltip.update({
        enabled: false,
    });
};
const resetToDefaultState = (chartTmp) => {
    const chart = getKpiHighchartObj(chartTmp);
    document.dispatchEvent(new CustomEvent('seriesselect', {
        detail: { labels: [], containerId: chart.container.id },
    }));
    const selectedPoints = chart.getSelectedPoints();
    if (selectedPoints.length > 0) {
        selectedPoints.forEach(p => {
            p.select(false);
        });
    }
    enableTooltip(chart);
    chart.series.forEach(s => {
        var _a;
        if (isPointNoOpacityByDefault(s)) {
            updateSerieFullOpacity(s);
            return;
        }
        const defaultOpacity = getDefaultPointOpacity(s);
        (_a = s.points) === null || _a === void 0 ? void 0 : _a.forEach(p => {
            updatePointOpacity(p, defaultOpacity);
        });
    });
    chart.ultraLightRedraw(false, true);
};
const onClickPointSelection = (chart) => {
    setTimeout(() => {
        const selectedPoints = chart.getSelectedPoints();
        if (selectedPoints.length === 0) {
            resetToDefaultState(chart);
            return;
        }
        const selectedSerieNames = selectedPoints.reduce((acc, p) => {
            if (!acc.includes(p.series.name))
                acc.push(p.series.name);
            return acc;
        }, []);
        document.dispatchEvent(new CustomEvent('seriesselect', {
            detail: {
                labels: selectedSerieNames,
                containerId: chart.container.id,
            },
        }));
        chart.series.forEach(s => {
            var _a;
            const isSerieSelected = selectedSerieNames.includes(s.name);
            if (isPointNoOpacityByDefault(s)) {
                if (isSerieSelected) {
                    updateSerieFullOpacity(s);
                }
                else {
                    updateSerieFadedOpacity(s);
                }
                return;
            }
            (_a = s.points) === null || _a === void 0 ? void 0 : _a.forEach(p => {
                if (p.selected) {
                    updatePointFullOpacity(p);
                }
                else {
                    updatePointFadedOpacity(p);
                }
            });
        });
        chart.ultraLightRedraw(false, true);
    }, 10);
};
const clickEvent = {
    click() {
        const chart = this.series.chart;
        if (getEnvFlags().isContextMenuEnabledOnLeftClick) {
            disableTooltip(chart);
            onClickPointSelection(chart);
            return;
        }
        enableTooltip(chart);
        onClickPointSelection(chart);
    },
};
const contextmenuEvent = {
    contextmenu() {
        const chart = this.series.chart;
        if (getEnvFlags().isContextMenuEnabledOnRightClick) {
            disableTooltip(chart);
            onClickPointSelection(chart);
            return;
        }
        enableTooltip(chart);
        onClickPointSelection(chart);
    },
};
const updateLegendOnSerieHover = _.debounce((label, containerId) => {
    document.dispatchEvent(new CustomEvent('seriehover', {
        detail: { label, containerId },
    }));
}, 50);
let previousPoint;
const setTooltipEnabledOnPointMouseOver = (point) => {
    const chart = point.series.chart;
    if (chart.options.tooltip.enabled === false &&
        previousPoint &&
        previousPoint !== point) {
        enableTooltip(chart);
    }
    previousPoint = point;
};
const pointMouseOverEvent = {
    mouseOver(event) {
        const point = event.target;
        updateLegendOnSerieHover(point.series.name, point.series.chart.container.id);
        if (point.series.chart.types.includes(SerieType.Pie)) {
            setPointHoverState(point);
        }
        const selectedPoints = point.series.chart.getSelectedPoints();
        if (selectedPoints.length === 0 || point.selected) {
            return;
        }
        applyPointOpacity(point, getDefaultPointOpacity(point.series));
        setTooltipEnabledOnPointMouseOver(point);
    },
};
const pointMouseOutEvent = {
    mouseOut(event) {
        const point = event.target;
        const chart = point.series.chart;
        updateLegendOnSerieHover('', chart.container.id);
        const selectedPoints = chart.getSelectedPoints();
        if (selectedPoints.length === 0 || point.selected) {
            return;
        }
        applyPointOpacity(point, getUnselectedPointOpacity(point.series));
    },
};
let previousSerie;
const setTooltipEnabledOnSerieMouseOver = (serie) => {
    const chart = serie.chart;
    if (chart.options.tooltip.enabled === false &&
        previousSerie &&
        previousSerie !== serie) {
        enableTooltip(chart);
    }
    previousSerie = serie;
};
const heatmapSeriesMouseOutEvent = {
    mouseOut(event) {
        const serie = event.target;
        if (serie.chart.getSelectedPoints().length === 0) {
            return;
        }
        setTimeout(() => {
            serie.points.forEach(p => {
                if (p.opacity === undefined ||
                    p.opacity === p.graphic.opacity ||
                    p.selected) {
                    return;
                }
                p.graphic.attr({
                    opacity: p.opacity,
                });
            });
        }, 50);
    },
};
const serieMouseOverEvent = {
    mouseOver(event) {
        const serie = event.target;
        const chart = serie.chart;
        updateLegendOnSerieHover(serie.name, chart.container.id);
        const selectedPoints = chart.getSelectedPoints();
        if (selectedPoints.length === 0) {
            return;
        }
        updateSerieFullOpacity(serie, true);
        setTooltipEnabledOnSerieMouseOver(serie);
    },
};
const serieMouseOutEvent = {
    mouseOut(event) {
        const serie = event.target;
        const chart = serie.chart;
        updateLegendOnSerieHover('', chart.container.id);
        const selectedPoints = chart.getSelectedPoints();
        if (selectedPoints.length === 0) {
            return;
        }
        const isSelected = selectedPoints.some(p => p.series.name === serie.name);
        if (isSelected) {
            return;
        }
        updateSerieFadedOpacity(serie, true);
    },
};
const onLegendHoverState = (bkChart, serieId, isHovered) => {
    const series = bkChart.getSeries();
    if (!series)
        return;
    const selectedPoints = bkChart.getSelectedPoints();
    if (selectedPoints.length === 0) {
        return;
    }
    if (isHovered) {
        series.forEach(serie => {
            var _a;
            if (isPointNoOpacityByDefault(serie)) {
                if (serie.name === serieId) {
                    updateSerieFullOpacity(serie);
                }
                else {
                    const isSelected = selectedPoints.some(p => p.series.name === serie.name);
                    if (isSelected) {
                        updateSerieFullOpacity(serie);
                    }
                    else {
                        updateSerieFadedOpacity(serie);
                    }
                }
                return;
            }
            const fullOpacity = getDefaultPointOpacity(series);
            const fadedOpacity = getUnselectedPointOpacity(series);
            (_a = serie.points) === null || _a === void 0 ? void 0 : _a.forEach(point => {
                if (serie.name === serieId || point.selected) {
                    updatePointOpacity(point, fullOpacity);
                }
                else {
                    updatePointOpacity(point, fadedOpacity);
                }
            });
        });
        bkChart.ultraLightRedraw();
        return;
    }
    series.forEach(serie => {
        var _a;
        if (isPointNoOpacityByDefault(serie)) {
            const isSelected = selectedPoints.some(p => p.series.name === serie.name);
            if (isSelected) {
                updateSerieFullOpacity(serie);
            }
            else {
                updateSerieFadedOpacity(serie);
            }
            return;
        }
        (_a = serie.points) === null || _a === void 0 ? void 0 : _a.forEach(point => {
            if (point.selected) {
                updatePointOpacity(point, fullOpacity);
            }
            else {
                updatePointOpacity(point, fadedOpacity);
            }
        });
    });
    bkChart.ultraLightRedraw();
};
const postProcessHighchartForApplyOpacity = (chart) => {
    if (!chartTypeRequiredToApplyOpacity(chart)) {
        return;
    }
    chart.series.forEach(s => {
        var _a;
        if (s.type === SerieType.Line) {
            if (s.options.dataLabels.enabled) {
                s.dataLabelsGroup.css({
                    opacity: s.opacity,
                });
            }
            return;
        }
        (_a = s.points) === null || _a === void 0 ? void 0 : _a.forEach(p => {
            if (isHeatMapChart(chart)) {
                if (p.selected) {
                    addStroke(p);
                    return;
                }
            }
            if (!p.opacity || !p.graphic || p.opacity === p.graphic.opacity) {
                return;
            }
            p.graphic.attr({
                opacity: p.opacity,
            });
        });
    });
};
const chartOutsideClickHandler = (chart, event) => {
    var _a, _b;
    let blinkHighChart = (_a = chart.current) === null || _a === void 0 ? void 0 : _a.chart;
    if (((_b = chart.current) === null || _b === void 0 ? void 0 : _b.chart) && chart.current.chart.getHighchartObj) {
        blinkHighChart = chart.current.chart.getHighchartObj();
    }
    const chartContainer = blinkHighChart === null || blinkHighChart === void 0 ? void 0 : blinkHighChart.container;
    if (chartContainer) {
        const isInsideChartArea = chartContainer.contains(event.target);
        if (isInsideChartArea) {
            const isInsidePlotArea = event.chartX >= blinkHighChart.plotLeft &&
                event.chartX <=
                    blinkHighChart.plotLeft + blinkHighChart.plotWidth &&
                event.chartY >= blinkHighChart.plotTop &&
                event.chartY <=
                    blinkHighChart.plotTop + blinkHighChart.plotHeight;
            if (isInsidePlotArea) {
                return;
            }
        }
        const selectedPoints = blinkHighChart.getSelectedPoints();
        if (selectedPoints.length > 0) {
            resetToDefaultState(blinkHighChart);
        }
        const emptyTooltipTemplate = '<div></div>';
        if (window.tooltipTemplate &&
            window.tooltipTemplate !== emptyTooltipTemplate) {
            const tooltipDiv = document.querySelector('.highcharts-tooltip');
            if (tooltipDiv) {
                const style = window.getComputedStyle(tooltipDiv);
                if (style.visibility === 'hidden' || style.display === 'none') {
                    setTimeout(() => {
                        window.tooltipTemplate = emptyTooltipTemplate;
                    }, 50);
                }
            }
        }
    }
};
export { addStroke, getBorderStyles, borderColor, borderWidth, fullOpacity, fadedOpacity, markerRadiusProps, inactiveStateProps, clipProps, hoverLineMarkerStateProps, hoverColumnStateProps, hoverColumnLineStateProps, selectLineMarkerStateProps, selectColumnStateProps, selectColumnLineStateProps, clickEvent, contextmenuEvent, resetToDefaultState, pointMouseOverEvent, pointMouseOutEvent, heatmapSeriesMouseOutEvent, serieMouseOverEvent, serieMouseOutEvent, onLegendHoverState, enableTooltip, disableTooltip, isAccumulateClick, postProcessHighchartForApplyOpacity, updateClipProperty, setPointHoverState, chartOutsideClickHandler, };
//# sourceMappingURL=util.js.map